import React from 'react'
import {
  useSuspenseInfiniteQuery,
  useSuspenseQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import { useMedplum } from '@medplum/react'
import type {
  DiagnosticReport,
  DocumentReference,
  Practitioner,
  Reference,
  ServiceRequest,
  Task,
} from '@medplum/fhirtypes'
import { notifications } from '@mantine/notifications'
import { OperationOutcomeError, preconditionFailed, WithId } from '@medplum/core'
import { useMiddlewareClient } from './useMiddlewareClient'
import { useClient } from './useClient'
import { faxPatientsQueryKey } from './useFaxPatients'
import { logError } from '@/utils/errorUtils'

const FAX_SENDER_PHONE_URL = 'https://behuman.health/fax-sender-phone'
const FAX_PAGE_COUNT_URL = 'https://behuman.health/fax-page-count'
export const FAX_TASK_TYPE = 'https://behuman.health/task-type|fax-triage'

// Referring provider is stored on a ServiceRequest (linked via DiagnosticReport.basedOn)
// in this extension by the fax triage handler — keep in sync with the middleware constant.
const FAX_AUTHORIZED_BY_EXT =
  'https://behuman.health/fhir/StructureDefinition/servicerequest-authorizedBy'

export type OcrStatus = 'pending' | 'completed' | 'failed' | 'not-run'

export type OcrData = {
  status: OcrStatus
  labName: string | null
  patientName: string | null
  dob: string | null
  gender: string | null
  practitionerName: string | null
}

export type FaxDetail = {
  task: Task
  documentReference: DocumentReference
  senderPhone: string
  pageCount: number
  receivedAt: string
  status: string
  contentType: string
  attachmentUrl: string
  assignedPatientId?: string
  assignedPatientName?: string
}

export type FaxInboxItem = {
  taskId: string
  documentReferenceId?: string
  senderPhone: string
  pageCount: number
  receivedAt: string
  status: string
}

type FaxInboxParams = {
  status?: string
  count?: number
  offset?: number
}

export const faxInboxQueryKey = (params: FaxInboxParams) => ['fax-inbox', params] as const

function taskToFaxInboxItem(task: Task): FaxInboxItem {
  const senderPhone =
    task.extension?.find((e) => e.url === FAX_SENDER_PHONE_URL)?.valueString ?? 'unknown'
  const pageCount = task.extension?.find((e) => e.url === FAX_PAGE_COUNT_URL)?.valueInteger ?? 0
  const receivedAt = task.authoredOn ?? task.meta?.lastUpdated ?? ''
  const documentReferenceId = task.focus?.reference?.startsWith('DocumentReference/')
    ? task.focus.reference.split('/')[1]
    : undefined

  return {
    taskId: task.id!,
    documentReferenceId,
    senderPhone,
    pageCount,
    receivedAt,
    status: task.status ?? 'requested',
  }
}

export function useFaxInbox(params: FaxInboxParams = {}) {
  const medplum = useMedplum()
  const count = params.count ?? 50

  return useSuspenseInfiniteQuery({
    queryKey: faxInboxQueryKey(params),
    queryFn: async ({ pageParam }) => {
      const searchParams = new URLSearchParams({
        code: FAX_TASK_TYPE,
        _count: String(count),
        _offset: String(pageParam * count),
        _sort: '-_lastUpdated',
      })
      if (params.status) {
        searchParams.set('status', params.status)
      }

      const bundle = await medplum.search('Task', searchParams)
      const tasks =
        bundle.entry?.map((e) => e.resource as Task).filter((t): t is Task => t != null) ?? []
      const items = tasks.map(taskToFaxInboxItem)
      const hasNextPage = !!bundle.link?.find((l) => l.relation === 'next')

      return { items, hasNextPage }
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage.hasNextPage) return undefined
      return allPages.length
    },
  })
}

type FaxDetailData = {
  task: WithId<Task>
}

export function useDeleteFax({ task }: FaxDetailData) {
  const medplum = useMedplum()
  const middlewareClient = useMiddlewareClient()
  const orpcClient = useClient()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      // Clean up OCR cache from middleware
      const docRefId = (task.focus as { reference?: string })?.reference?.split('/')[1]
      if (docRefId) {
        // Drop the fax-triage Task id from any care task created off this fax
        // (PD-1513): that id is what their "From an incoming fax" link points
        // at, and the Task is about to stop existing. Done BEFORE the delete and
        // allowed to abort it — a fax that still exists with an intact link can
        // simply be deleted again, whereas a deleted fax leaves those links
        // permanently broken with nothing left to repair them from. It also refuses
        // a fax mid-split (PD-1988), so it runs before the OCR results are cleared.
        await orpcClient.detachFaxTasks({ documentReferenceId: docRefId })
        await middlewareClient.delete(`/order/documents/${docRefId}/ocr-results`)
      }
      // Delete the task from Medplum
      await medplum.deleteResource('Task', task.id)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fax-inbox'] })
    },
    onError: (error) => {
      queryClient.invalidateQueries({ queryKey: ['fax-inbox'] })
      queryClient.invalidateQueries({ queryKey: faxDetailQueryKey(task.id) })
      // An unfinished split refuses the delete (PD-1988); the refetch brings up "Finish filing".
      void queryClient.invalidateQueries({ queryKey: faxPatientsQueryKey(task.id) })
      const refusal = (error as { code?: unknown }).code === 'CONFLICT' ? error.message : undefined
      notifications.show({
        color: 'red',
        title: 'Delete failed',
        message: refusal || 'Failed to delete the fax. Please try again.',
      })
    },
  })
}

export const faxDetailQueryKey = (taskId: string) => ['fax-detail', taskId] as const

export function useMarkFaxViewed(task: WithId<Task>) {
  const medplum = useMedplum()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      if (task.status !== 'requested') return
      try {
        // If-Match: a stale copy of the task must not overwrite a split claim (PD-1988)
        // or any other change made since this page loaded.
        await medplum.updateResource(
          { ...task, status: 'in-progress' },
          { headers: { 'If-Match': `W/"${task.meta?.versionId}"` } },
        )
      } catch (err) {
        // Someone else already changed the fax; the refetch below shows where it stands.
        if (!(err instanceof OperationOutcomeError && err.outcome.id === preconditionFailed.id)) {
          throw err
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fax-inbox-unread'] })
      queryClient.invalidateQueries({ queryKey: faxDetailQueryKey(task.id) })
    },
  })
}

const OCR_POLL_TIMEOUT_MS = 30_000 // Stop polling after 30 seconds

export function useFaxDetail(taskId: string) {
  const medplum = useMedplum()
  const middlewareClient = useMiddlewareClient()
  const pendingStartTimeRef = React.useRef<number | null>(null)

  return useSuspenseQuery({
    queryKey: faxDetailQueryKey(taskId),
    queryFn: async () => {
      const task = await medplum.readResource('Task', taskId)

      if (!task.focus) throw new Error('Task has no focus reference')

      const documentReference = await medplum.readReference(
        task.focus as Reference<DocumentReference>,
      )

      const attachment = documentReference.content?.[0]?.attachment

      if (!attachment) throw new Error('DocumentReference has no attachment')

      const drRef = task.output?.find((o) => o.type?.text === 'DiagnosticReport')?.valueReference
      const diagnosticReport: DiagnosticReport | undefined =
        task.status === 'completed' && drRef
          ? await medplum.readReference(drRef as Reference<DiagnosticReport>)
          : undefined

      // The referring provider lives on the ServiceRequest linked via DiagnosticReport.basedOn,
      // in the `servicerequest-authorizedBy` extension (mirrors triage write + LabResults read).
      const serviceRequestRef = diagnosticReport?.basedOn?.find((ref) =>
        ref.reference?.startsWith('ServiceRequest/'),
      )
      const serviceRequest: ServiceRequest | undefined = serviceRequestRef
        ? await medplum.readReference(serviceRequestRef as Reference<ServiceRequest>)
        : undefined
      const referringProvider = serviceRequest?.extension?.find(
        (e) => e.url === FAX_AUTHORIZED_BY_EXT,
      )?.valueReference as Reference<Practitioner> | undefined

      // Fetch OCR results from middleware. OCR suggestions are optional — if the
      // middleware is unavailable, degrade to 'not-run' rather than crashing the
      // whole fax/triage view (this runs inside a Suspense query).
      const ocrData: OcrData = await middlewareClient
        .get(`/order/documents/{documentReferenceId}/ocr-results`, {
          documentReferenceId: documentReference.id,
        })
        .then((r) => r as OcrData)
        .catch((err) => {
          // Visible in console + Sentry, but the view still degrades gracefully.
          logError('fax', 'Failed to fetch OCR suggestions; degrading to not-run', err)
          return {
            status: 'not-run' as const,
            labName: null,
            patientName: null,
            dob: null,
            gender: null,
            practitionerName: null,
          }
        })

      return { task, documentReference, attachment, diagnosticReport, referringProvider, ocrData }
    },
    refetchInterval: (query) => {
      const isPending = query.state.data?.ocrData?.status === 'pending'

      if (!isPending) {
        pendingStartTimeRef.current = null
        return false
      }

      // Track when pending started and stop polling after timeout
      if (!pendingStartTimeRef.current) {
        pendingStartTimeRef.current = Date.now()
      }

      const elapsedMs = Date.now() - pendingStartTimeRef.current
      if (elapsedMs > OCR_POLL_TIMEOUT_MS) {
        return false
      }

      return 2000
    },
  })
}
